// src/pages/CoupleDashboard.js
// Updated with tabs for both 20-Factor Assessment and Pre-Marriage Intensive
// Added: Two prayer buttons (Deep Prayer & Quick Prayer) for Intensive modules

import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Heart, 
  LogOut, 
  Clock, 
  CheckCircle, 
  XCircle, 
  AlertCircle, 
  BookOpen, 
  Heart as PrayerIcon,
  Lock,
  FileQuestion,
  Play,
  Award,
  ClipboardList,
  Zap  // Added for Quick Prayer button
} from 'lucide-react';
import { useAssessment } from '../hooks/useAssessment';
import { useCouple } from '../hooks/useCouple';
import { useAuth } from '../contexts/AuthContext';
import { signOut } from '../services/authService';
import LoadingSpinner from '../components/LoadingSpinner';
import TeachingPresentation from '../components/TeachingPresentation';
import PrayerPresentation from '../components/PrayerPresentation';
import IntensiveTeachingPresentation from '../components/IntensiveTeachingPresentation';
import IntensivePrayerPresentation from '../components/IntensivePrayerPresentation';
import { FACTOR_NAMES } from '../data/factorInterpretations';
import { INTENSIVE_MODULE_DATA, INTENSIVE_MODULE_NAMES } from '../data/intensiveModules';

const CoupleDashboard = () => {
  const navigate = useNavigate();
  const { userProfile } = useAuth();
  const { assessment, loading: assessmentLoading } = useAssessment();
  const { couple, partnerAssessment, loading: coupleLoading } = useCouple();
  
  // Tab state: 'intensive' for 10-module, 'assessment' for 20-factor
  const [activeTab, setActiveTab] = useState('intensive');
  
  // Modal states for 20-factor
  const [showTeaching, setShowTeaching] = useState(false);
  const [showPrayer, setShowPrayer] = useState(false);
  const [selectedFactor, setSelectedFactor] = useState(null);
  
  // Modal states for intensive
  const [showIntensiveTeaching, setShowIntensiveTeaching] = useState(false);
  const [showIntensivePrayer, setShowIntensivePrayer] = useState(false);
  const [selectedModule, setSelectedModule] = useState(null);
  const [intensivePrayerMode, setIntensivePrayerMode] = useState('long'); // NEW: 'long' or 'short'

  const handleSignOut = async () => {
    await signOut();
    navigate('/');
  };

  // 20-Factor handlers
  const handleOpenTeaching = (factorNumber) => {
    setSelectedFactor(factorNumber);
    setShowTeaching(true);
  };

  const handleOpenPrayer = (factorNumber) => {
    setSelectedFactor(factorNumber);
    setShowPrayer(true);
  };

  const handleTakeQuiz = (factorNumber) => {
    navigate(`/quiz?factor=${factorNumber}`);
  };

  // Intensive handlers
  const handleOpenIntensiveTeaching = (moduleNumber) => {
    setSelectedModule(moduleNumber);
    setShowIntensiveTeaching(true);
  };

  // UPDATED: Now accepts mode parameter ('long' or 'short')
  const handleOpenIntensivePrayer = (moduleNumber, mode) => {
    setSelectedModule(moduleNumber);
    setIntensivePrayerMode(mode);
    setShowIntensivePrayer(true);
  };

  const handleTakeIntensiveQuiz = (moduleNumber) => {
    navigate(`/intensive/quiz?module=${moduleNumber}`);
  };

  if (assessmentLoading || coupleLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <LoadingSpinner />
          <p className="mt-4 text-gray-600">Loading your dashboard...</p>
        </div>
      </div>
    );
  }

  const approvalStatus = userProfile?.approvalStatus || couple?.approvalStatus || 'pending';
  const isApproved = approvalStatus === 'approved';
  const isPending = approvalStatus === 'pending';
  const isRejected = approvalStatus === 'rejected';

  // 20-Factor data
  const completedFactors = assessment?.completedFactors || [];
  const factorPermissions = couple?.factorPermissions || {};
  const factorScores = assessment?.factorScores || {};

  // Intensive data
  const completedModules = assessment?.completedIntensiveModules || [];
  const intensivePermissions = couple?.intensivePermissions || {};
  const intensiveScores = assessment?.intensiveModuleScores || {};

  // Get factor status
  const getFactorStatus = (factorNumber) => {
    const permission = factorPermissions[factorNumber] || {};
    const isUnlocked = permission.canTake === true;
    const isCompleted = completedFactors.includes(factorNumber);
    
    return {
      isUnlocked,
      isCompleted,
      isLocked: !isUnlocked,
      score: factorScores[factorNumber]
    };
  };

  // Get module status
  const getModuleStatus = (moduleNumber) => {
    const permission = intensivePermissions[moduleNumber] || {};
    const isUnlocked = permission.canTake === true;
    const isCompleted = completedModules.includes(moduleNumber);
    const moduleData = INTENSIVE_MODULE_DATA[moduleNumber];
    const maxScore = moduleData?.questions?.length * 5 || 80;
    
    return {
      isUnlocked,
      isCompleted,
      isLocked: !isUnlocked,
      score: intensiveScores[moduleNumber],
      maxScore
    };
  };

  return (
    <>
      {/* 20-Factor Teaching Modal */}
      {showTeaching && selectedFactor && (
        <TeachingPresentation
          factorNumber={selectedFactor}
          onClose={() => setShowTeaching(false)}
        />
      )}

      {/* 20-Factor Prayer Modal */}
      {showPrayer && selectedFactor && (
        <PrayerPresentation
          factorNumber={selectedFactor}
          onClose={() => setShowPrayer(false)}
        />
      )}

      {/* Intensive Teaching Modal */}
      {showIntensiveTeaching && selectedModule && (
        <IntensiveTeachingPresentation
          moduleNumber={selectedModule}
          onClose={() => setShowIntensiveTeaching(false)}
        />
      )}

      {/* Intensive Prayer Modal - UPDATED: Now passes initialMode */}
      {showIntensivePrayer && selectedModule && (
        <IntensivePrayerPresentation
          moduleNumber={selectedModule}
          initialMode={intensivePrayerMode}
          onClose={() => setShowIntensivePrayer(false)}
        />
      )}

      <div className="min-h-screen bg-gray-50">
        {/* Navigation */}
        <nav className="bg-white shadow-sm border-b">
          <div className="max-w-7xl mx-auto px-4 py-4 flex justify-between items-center">
            <div className="flex items-center gap-2">
              <Heart className="w-6 h-6 text-purple-600" />
              <span className="font-semibold text-lg">HeartSync Assessment</span>
            </div>
            <button 
              onClick={handleSignOut}
              className="flex items-center gap-2 text-gray-600 hover:text-gray-800"
            >
              <LogOut className="w-4 h-4" />
              Sign Out
            </button>
          </div>
        </nav>

        <div className="max-w-4xl mx-auto p-6 py-8">
          {/* Header */}
          <div className="mb-6">
            <h1 className="text-2xl sm:text-3xl font-bold mb-2">Welcome, {userProfile?.name || 'User'}</h1>
            <p className="text-gray-600">
              {couple ? `You and ${userProfile?.partnerName}` : 'Your Assessment Dashboard'}
            </p>
          </div>

          {/* Pending Approval Status */}
          {isPending && (
            <div className="mb-8 p-6 bg-orange-50 border-2 border-orange-300 rounded-xl">
              <div className="flex items-start gap-4">
                <Clock className="w-8 h-8 text-orange-600 flex-shrink-0 mt-1" />
                <div className="flex-1">
                  <h2 className="text-xl font-bold text-orange-900 mb-2">
                    Pending Counselor Approval
                  </h2>
                  <p className="text-orange-800 mb-4">
                    Your registration has been submitted! Your counselor needs to approve 
                    your registration before you can start the assessment.
                  </p>
                  <div className="p-4 bg-white rounded-lg border border-orange-200">
                    <h3 className="font-semibold text-sm text-orange-900 mb-2">What happens next?</h3>
                    <ul className="text-sm text-orange-800 space-y-2">
                      <li className="flex items-start gap-2">
                        <span className="text-orange-600 font-bold">1.</span>
                        <span>Your counselor will review your registration</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="text-orange-600 font-bold">2.</span>
                        <span>Once approved, you can start your assessment</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="text-orange-600 font-bold">3.</span>
                        <span>Check back here or contact your counselor for updates</span>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Rejected Status */}
          {isRejected && (
            <div className="mb-8 p-6 bg-red-50 border-2 border-red-300 rounded-xl">
              <div className="flex items-start gap-4">
                <XCircle className="w-8 h-8 text-red-600 flex-shrink-0 mt-1" />
                <div className="flex-1">
                  <h2 className="text-xl font-bold text-red-900 mb-2">
                    Registration Not Approved
                  </h2>
                  <p className="text-red-800">
                    Your counselor has not approved your registration. Please contact them for more information.
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Main Content - Only show if approved */}
          {isApproved && (
            <>
              {/* Tab Navigation */}
              <div className="mb-6 bg-white rounded-xl shadow-sm border p-1 flex">
                <button
                  onClick={() => setActiveTab('intensive')}
                  className={`flex-1 flex items-center justify-center gap-2 py-3 px-4 rounded-lg font-semibold transition ${
                    activeTab === 'intensive'
                      ? 'bg-emerald-600 text-white'
                      : 'text-gray-600 hover:bg-gray-100'
                  }`}
                >
                  <Award className="w-5 h-5" />
                  <span className="hidden sm:inline">Pre-Marriage Intensive</span>
                  <span className="sm:hidden">Intensive</span>
                  <span className={`text-xs px-2 py-0.5 rounded-full ${
                    activeTab === 'intensive' ? 'bg-emerald-500' : 'bg-gray-200'
                  }`}>
                    {completedModules.length}/10
                  </span>
                </button>
                <button
                  onClick={() => setActiveTab('assessment')}
                  className={`flex-1 flex items-center justify-center gap-2 py-3 px-4 rounded-lg font-semibold transition ${
                    activeTab === 'assessment'
                      ? 'bg-purple-600 text-white'
                      : 'text-gray-600 hover:bg-gray-100'
                  }`}
                >
                  <ClipboardList className="w-5 h-5" />
                  <span className="hidden sm:inline">20-Factor Assessment</span>
                  <span className="sm:hidden">Assessment</span>
                  <span className={`text-xs px-2 py-0.5 rounded-full ${
                    activeTab === 'assessment' ? 'bg-purple-500' : 'bg-gray-200'
                  }`}>
                    {completedFactors.length}/20
                  </span>
                </button>
              </div>

              {/* ===== INTENSIVE TAB CONTENT ===== */}
              {activeTab === 'intensive' && (
                <div className="bg-white p-6 sm:p-8 rounded-xl shadow-sm border">
                  <div className="flex items-center gap-3 mb-6">
                    <div className="w-12 h-12 bg-emerald-100 rounded-full flex items-center justify-center">
                      <Award className="w-6 h-6 text-emerald-600" />
                    </div>
                    <div>
                      <h2 className="text-xl font-semibold">Pre-Marriage Intensive</h2>
                      <p className="text-sm text-gray-600">10 modules • 160 questions</p>
                    </div>
                  </div>

                  {/* Progress Bar */}
                  <div className="mb-6">
                    <div className="flex justify-between text-sm mb-2">
                      <span className="text-gray-600">Progress</span>
                      <span className="font-medium text-emerald-600">
                        {completedModules.length} of 10 modules completed
                      </span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-3">
                      <div
                        className="bg-gradient-to-r from-emerald-500 to-teal-500 h-3 rounded-full transition-all duration-500"
                        style={{ width: `${(completedModules.length / 10) * 100}%` }}
                      />
                    </div>
                  </div>

                  {/* Module Grid */}
                  <div className="space-y-4">
                    {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((moduleNumber) => {
                      const moduleData = INTENSIVE_MODULE_DATA[moduleNumber];
                      const { isUnlocked, isCompleted, isLocked, score, maxScore } = getModuleStatus(moduleNumber);

                      return (
                        <div
                          key={moduleNumber}
                          className={`p-4 rounded-xl border-2 transition ${
                            isCompleted ? 'bg-emerald-50 border-emerald-300' :
                            isUnlocked ? 'bg-white border-emerald-200 hover:border-emerald-400' :
                            'bg-gray-50 border-gray-200'
                          }`}
                        >
                          <div className="flex items-start justify-between mb-3">
                            <div className="flex items-start gap-3">
                              <div className={`w-10 h-10 rounded-full flex items-center justify-center text-lg ${
                                isCompleted ? 'bg-emerald-500 text-white' :
                                isUnlocked ? 'bg-emerald-100 text-emerald-600' :
                                'bg-gray-200 text-gray-400'
                              }`}>
                                {isCompleted ? (
                                  <CheckCircle className="w-5 h-5" />
                                ) : isLocked ? (
                                  <Lock className="w-5 h-5" />
                                ) : (
                                  moduleData?.icon || moduleNumber
                                )}
                              </div>
                              
                              <div>
                                <div className="flex items-center gap-2 flex-wrap">
                                  <h3 className={`font-bold ${isLocked ? 'text-gray-500' : 'text-gray-900'}`}>
                                    Module {moduleNumber}
                                  </h3>
                                  {isCompleted ? (
                                    <span className="px-2 py-0.5 text-xs font-semibold bg-emerald-200 text-emerald-800 rounded-full">
                                      ✓ Completed
                                    </span>
                                  ) : isUnlocked ? (
                                    <span className="px-2 py-0.5 text-xs font-semibold bg-emerald-100 text-emerald-700 rounded-full">
                                      🔓 Unlocked
                                    </span>
                                  ) : (
                                    <span className="px-2 py-0.5 text-xs font-semibold bg-gray-200 text-gray-600 rounded-full">
                                      🔒 Locked
                                    </span>
                                  )}
                                </div>
                                <p className={`text-sm ${isLocked ? 'text-gray-400' : 'text-gray-600'}`}>
                                  {moduleData?.shortName || INTENSIVE_MODULE_NAMES[moduleNumber - 1]}
                                </p>
                              </div>
                            </div>

                            {/* Score (if completed) */}
                            {isCompleted && score !== undefined && (
                              <div className="text-right">
                                <div className="text-xl font-bold text-emerald-600">
                                  {score}/{maxScore}
                                </div>
                                <div className="text-xs text-gray-500">Score</div>
                              </div>
                            )}
                          </div>

                          {/* Action Buttons */}
                          <div className="flex flex-wrap gap-2">
                            {/* Teaching */}
                            {isUnlocked || isCompleted ? (
                              <button
                                onClick={() => handleOpenIntensiveTeaching(moduleNumber)}
                                className="flex items-center gap-2 px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition text-sm"
                              >
                                <BookOpen className="w-4 h-4" />
                                Teaching
                              </button>
                            ) : (
                              <div className="flex items-center gap-2 px-3 py-2 bg-gray-300 text-gray-500 rounded-lg text-sm cursor-not-allowed">
                                <Lock className="w-4 h-4" />
                                Teaching
                              </div>
                            )}

                            {/* ===== TWO PRAYER BUTTONS (Deep Prayer & Quick Prayer) ===== */}
                            {isUnlocked || isCompleted ? (
                              <>
                                {/* Deep Prayer Button - Purple */}
                                <button
                                  onClick={() => handleOpenIntensivePrayer(moduleNumber, 'long')}
                                  className="flex items-center gap-2 px-3 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition text-sm"
                                >
                                  <PrayerIcon className="w-4 h-4" />
                                  Deep Prayer
                                </button>
                                
                                {/* Quick Prayer Button - Amber/Gold */}
                                <button
                                  onClick={() => handleOpenIntensivePrayer(moduleNumber, 'short')}
                                  className="flex items-center gap-2 px-3 py-2 bg-amber-500 text-white rounded-lg hover:bg-amber-600 transition text-sm"
                                >
                                  <Zap className="w-4 h-4" />
                                  Quick Prayer
                                </button>
                              </>
                            ) : (
                              <div className="flex items-center gap-2 px-3 py-2 bg-gray-300 text-gray-500 rounded-lg text-sm cursor-not-allowed">
                                <Lock className="w-4 h-4" />
                                Prayer
                              </div>
                            )}

                            {/* Take Quiz / View Results */}
                            {isCompleted ? (
                              <button
                                onClick={() => navigate(`/intensive/results/${moduleNumber}`)}
                                className="flex items-center gap-2 px-3 py-2 bg-emerald-100 text-emerald-700 rounded-lg hover:bg-emerald-200 transition text-sm border border-emerald-300"
                              >
                                <CheckCircle className="w-4 h-4" />
                                View Results
                              </button>
                            ) : isUnlocked ? (
                              <button
                                onClick={() => handleTakeIntensiveQuiz(moduleNumber)}
                                className="flex items-center gap-2 px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition text-sm font-semibold"
                              >
                                <Play className="w-4 h-4" />
                                Take Quiz
                              </button>
                            ) : (
                              <div className="flex items-center gap-2 px-3 py-2 bg-gray-300 text-gray-500 rounded-lg text-sm cursor-not-allowed">
                                <Lock className="w-4 h-4" />
                                Take Quiz
                              </div>
                            )}
                          </div>

                          {/* Locked Message */}
                          {isLocked && (
                            <div className="mt-3 p-3 bg-gray-100 rounded-lg border border-gray-200">
                              <p className="text-sm text-gray-500 flex items-center gap-2">
                                <AlertCircle className="w-4 h-4" />
                                This module is locked. Your counselor will unlock it when you're ready.
                              </p>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* ===== 20-FACTOR TAB CONTENT ===== */}
              {activeTab === 'assessment' && (
                <div className="bg-white p-6 sm:p-8 rounded-xl shadow-sm border">
                  <div className="flex items-center gap-3 mb-6">
                    <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center">
                      <ClipboardList className="w-6 h-6 text-purple-600" />
                    </div>
                    <div>
                      <h2 className="text-xl font-semibold">20-Factor Assessment</h2>
                      <p className="text-sm text-gray-600">20 factors • 200 questions</p>
                    </div>
                  </div>

                  {/* Progress Bar */}
                  <div className="mb-6">
                    <div className="flex justify-between text-sm mb-2">
                      <span className="text-gray-600">Progress</span>
                      <span className="font-medium text-purple-600">
                        {completedFactors.length} of 20 factors completed
                      </span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-3">
                      <div
                        className="bg-gradient-to-r from-purple-500 to-blue-500 h-3 rounded-full transition-all duration-500"
                        style={{ width: `${(completedFactors.length / 20) * 100}%` }}
                      />
                    </div>
                  </div>

                  {/* Factor Grid */}
                  <div className="space-y-4">
                    {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20].map((factorNumber) => {
                      const factorName = FACTOR_NAMES[factorNumber] || `Factor ${factorNumber}`;
                      const { isUnlocked, isCompleted, isLocked, score } = getFactorStatus(factorNumber);

                      return (
                        <div
                          key={factorNumber}
                          className={`p-4 rounded-xl border-2 transition ${
                            isCompleted ? 'bg-purple-50 border-purple-300' :
                            isUnlocked ? 'bg-white border-purple-200 hover:border-purple-400' :
                            'bg-gray-50 border-gray-200'
                          }`}
                        >
                          <div className="flex items-start justify-between mb-3">
                            <div className="flex items-start gap-3">
                              <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold ${
                                isCompleted ? 'bg-purple-500 text-white' :
                                isUnlocked ? 'bg-purple-100 text-purple-600' :
                                'bg-gray-200 text-gray-400'
                              }`}>
                                {isCompleted ? (
                                  <CheckCircle className="w-5 h-5" />
                                ) : isLocked ? (
                                  <Lock className="w-5 h-5" />
                                ) : (
                                  factorNumber
                                )}
                              </div>
                              
                              <div>
                                <div className="flex items-center gap-2 flex-wrap">
                                  <h3 className={`font-bold ${isLocked ? 'text-gray-500' : 'text-gray-900'}`}>
                                    Factor {factorNumber}
                                  </h3>
                                  {isCompleted ? (
                                    <span className="px-2 py-0.5 text-xs font-semibold bg-purple-200 text-purple-800 rounded-full">
                                      ✓ Completed
                                    </span>
                                  ) : isUnlocked ? (
                                    <span className="px-2 py-0.5 text-xs font-semibold bg-purple-100 text-purple-700 rounded-full">
                                      🔓 Unlocked
                                    </span>
                                  ) : (
                                    <span className="px-2 py-0.5 text-xs font-semibold bg-gray-200 text-gray-600 rounded-full">
                                      🔒 Locked
                                    </span>
                                  )}
                                </div>
                                <p className={`text-sm ${isLocked ? 'text-gray-400' : 'text-gray-600'}`}>
                                  {factorName}
                                </p>
                              </div>
                            </div>

                            {/* Score (if completed) */}
                            {isCompleted && score !== undefined && (
                              <div className="text-right">
                                <div className="text-xl font-bold text-purple-600">
                                  {score}/50
                                </div>
                                <div className="text-xs text-gray-500">Score</div>
                              </div>
                            )}
                          </div>

                          {/* Action Buttons */}
                          <div className="flex flex-wrap gap-2">
                            {/* Preview Questions */}
                            {isUnlocked || isCompleted ? (
                              <button
                                onClick={() => handleTakeQuiz(factorNumber)}
                                className="flex items-center gap-2 px-3 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition text-sm"
                              >
                                <FileQuestion className="w-4 h-4" />
                                Preview
                              </button>
                            ) : (
                              <div className="flex items-center gap-2 px-3 py-2 bg-gray-300 text-gray-500 rounded-lg text-sm cursor-not-allowed">
                                <Lock className="w-4 h-4" />
                                Preview
                              </div>
                            )}

                            {/* Teaching */}
                            {isUnlocked || isCompleted ? (
                              <button
                                onClick={() => handleOpenTeaching(factorNumber)}
                                className="flex items-center gap-2 px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition text-sm"
                              >
                                <BookOpen className="w-4 h-4" />
                                Teaching
                              </button>
                            ) : (
                              <div className="flex items-center gap-2 px-3 py-2 bg-gray-300 text-gray-500 rounded-lg text-sm cursor-not-allowed">
                                <Lock className="w-4 h-4" />
                                Teaching
                              </div>
                            )}

                            {/* Prayer */}
                            {isUnlocked || isCompleted ? (
                              <button
                                onClick={() => handleOpenPrayer(factorNumber)}
                                className="flex items-center gap-2 px-3 py-2 bg-pink-600 text-white rounded-lg hover:bg-pink-700 transition text-sm"
                              >
                                <PrayerIcon className="w-4 h-4" />
                                Prayer
                              </button>
                            ) : (
                              <div className="flex items-center gap-2 px-3 py-2 bg-gray-300 text-gray-500 rounded-lg text-sm cursor-not-allowed">
                                <Lock className="w-4 h-4" />
                                Prayer
                              </div>
                            )}

                            {/* Take Quiz / Completed */}
                            {isCompleted ? (
                              <div className="flex items-center gap-2 px-3 py-2 bg-green-100 text-green-700 rounded-lg text-sm border border-green-300">
                                <CheckCircle className="w-4 h-4" />
                                Completed
                              </div>
                            ) : isUnlocked ? (
                              <button
                                onClick={() => handleTakeQuiz(factorNumber)}
                                className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition text-sm font-semibold"
                              >
                                <Play className="w-4 h-4" />
                                Take Quiz
                              </button>
                            ) : (
                              <div className="flex items-center gap-2 px-3 py-2 bg-gray-300 text-gray-500 rounded-lg text-sm cursor-not-allowed">
                                <Lock className="w-4 h-4" />
                                Take Quiz
                              </div>
                            )}
                          </div>

                          {/* Locked Message */}
                          {isLocked && (
                            <div className="mt-3 p-3 bg-gray-100 rounded-lg border border-gray-200">
                              <p className="text-sm text-gray-500 flex items-center gap-2">
                                <AlertCircle className="w-4 h-4" />
                                This factor is locked. Your counselor will unlock it when you're ready.
                              </p>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Partner Status Card */}
              {couple && (
                <div className="mt-6 bg-white p-6 sm:p-8 rounded-xl shadow-sm border">
                  <h2 className="text-xl font-semibold mb-4">Partner Status</h2>
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
                      <Heart className="w-6 h-6 text-blue-600" />
                    </div>
                    <div>
                      <p className="font-semibold">{userProfile?.partnerName}</p>
                      <p className="text-sm text-gray-600">
                        {partnerAssessment?.status === 'completed' ? '✅ Completed Assessment' :
                         partnerAssessment?.status === 'in-progress' ? '⏳ In Progress' :
                         '⭕ Not Started'}
                      </p>
                    </div>
                  </div>

                  {couple.sharingEnabled && (
                    <div className="grid grid-cols-2 gap-4 mt-4">
                      <div className="p-4 bg-purple-50 rounded-lg border border-purple-200">
                        <p className="text-sm text-purple-600 mb-1">20-Factor Progress</p>
                        <p className="text-lg font-bold text-purple-700">
                          {partnerAssessment?.completedFactors?.length || 0}/20
                        </p>
                      </div>
                      <div className="p-4 bg-emerald-50 rounded-lg border border-emerald-200">
                        <p className="text-sm text-emerald-600 mb-1">Intensive Progress</p>
                        <p className="text-lg font-bold text-emerald-700">
                          {partnerAssessment?.completedIntensiveModules?.length || 0}/10
                        </p>
                      </div>
                    </div>
                  )}

                  {!couple.sharingEnabled && (
                    <div className="mt-4 p-4 bg-gray-50 rounded-lg border">
                      <p className="text-sm text-gray-600">
                        <AlertCircle className="w-4 h-4 inline mr-2" />
                        Result sharing is not enabled. Your counselor can enable this feature.
                      </p>
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </>
  );
};

export default CoupleDashboard;
